import React, { useState } from "react";
import {
  Box,
  ListItem,
  ListItemButton,
  Card,
  CardContent,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  Description as DocumentIcon,
  Movie as VideoIcon,
  AudioFile as AudioIcon,
  Archive as ArchiveIcon,
  Code as CodeIcon,
  GridOn as SpreadsheetIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { EditIcon, TrashCanIcon } from "@fluentui/react-northstar";
import { formatDate, formatFileSize } from "../utils/fileOperations";

const FileItem = ({
  item,
  viewType = "list",
  isSelected = false,
  onClick,
  divider = false,
  onRenameFolder,
  onDeleteFolder,
}) => {
  const { t } = useTranslation();
  const translation = t("fileExplorer");
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleRenameFolder = () => {
    handleMenuClose();
    onRenameFolder && onRenameFolder(item);
  };

  const handleDeleteFolder = () => {
    handleMenuClose();
    onDeleteFolder && onDeleteFolder(item);
  };
  const getFileIcon = (item) => {
    if (item.type === "folder") {
      return <FolderIcon color="primary" />;
    }

    const iconProps = { color: "action" };

    switch (item.type) {
      case "image":
        return <ImageIcon {...iconProps} />;
      case "document":
        return <DocumentIcon {...iconProps} />;
      case "spreadsheet":
        return <SpreadsheetIcon {...iconProps} />;
      case "video":
        return <VideoIcon {...iconProps} />;
      case "audio":
        return <AudioIcon {...iconProps} />;
      case "archive":
        return <ArchiveIcon {...iconProps} />;
      case "code":
        return <CodeIcon {...iconProps} />;
      default:
        return <FileIcon {...iconProps} />;
    }
  };

  if (viewType === "grid") {
    return (
      <Card
        variant="outlined"
        sx={{
          cursor: "pointer",
          transition: "all 0.2s",
          "&:hover": {
            boxShadow: 2,
            transform: "translateY(-2px)",
          },
          ...(isSelected && {
            borderColor: "primary.main",
            backgroundColor: "primary.50",
          }),
        }}
        onClick={(event) => onClick(item, event)}
        onContextMenu={(event) => onClick(item, event)}
      >
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1,
              justifyContent: "center",
            }}
          >
            {getFileIcon(item)}
          </Box>

          <Typography
            variant="body2"
            noWrap
            title={item.name}
            sx={{ textAlign: "center", fontWeight: 500 }}
          >
            {item.name}
          </Typography>

          {item.type !== "folder" && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textAlign: "center", display: "block", mt: 0.5 }}
            >
              {item.size}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <ListItem disablePadding divider={divider}>
      <ListItemButton
        onClick={(event) => onClick(item, event)}
        onContextMenu={(event) => onClick(item, event)}
        selected={isSelected}
        sx={{
          py: 1.5,
          px: 2,
          display: "flex",
          alignItems: "center",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", minWidth: 40, mr: 2 }}
        >
          {getFileIcon(item)}
        </Box>

        <Box sx={{ flex: "1 1 auto", mr: 2, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: 500 }}
            title={item.name}
          >
            {item.name}
          </Typography>
          {/* Subline for mobile with condensed meta */}
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <Typography variant="caption" color="text.secondary" noWrap>
              {item.type}
              {item.type !== "folder" && typeof item.size !== "undefined"
                ? ` • ${formatFileSize(item.size || "-")}`
                : ""}
              {item.updatedAt ? " • " + formatDate(item.updatedAt) : ""}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            flex: "0 0 100px",
            mr: 2,
            display: { xs: "none", sm: "block" },
          }}
        >
          <Typography variant="body2" color="text.secondary" noWrap>
            {item.type}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: "0 0 120px",
            mr: 2,
            display: { xs: "none", sm: "block" },
          }}
        >
          <Typography variant="body2" color="text.secondary" noWrap>
            {item.owner || translation.me}
          </Typography>
        </Box>

        <Box
          sx={{ flex: "0 0 80px", mr: 2, display: { xs: "none", sm: "block" } }}
        >
          <Typography variant="body2" color="text.secondary" noWrap>
            {formatFileSize(item.type !== "folder" ? item.size || "-" : "-")}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: "0 0 120px",
            mr: 1,
            display: { xs: "none", sm: "block" },
          }}
        >
          <Typography variant="body2" color="text.secondary" noWrap>
            {formatDate(item.updatedAt)}
          </Typography>
        </Box>

        {item.type === "folder" && (
          <Box sx={{ minWidth: 40 }}>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              aria-label={translation.folderOptions}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuItem onClick={handleRenameFolder}>
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={translation.renameFolder} />
              </MenuItem>
              <MenuItem
                onClick={handleDeleteFolder}
                sx={{ color: "error.main" }}
              >
                <ListItemIcon>
                  <TrashCanIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={translation.moveToRecycleBin} />
              </MenuItem>
            </Menu>
          </Box>
        )}

        {item.type !== "folder" && <Box sx={{ minWidth: 40 }}></Box>}
      </ListItemButton>
    </ListItem>
  );
};

export default FileItem;
